/**
 * ConciergeHero — centered hero atom shared across /enjoy, /escape (and
 * eventually /table).
 *
 * Founder ruling 18 Feb 2026 PM-9 (post Phase-4):
 *   "/escape must feel like the same Concierge® product family as /enjoy.
 *    Same centred hero, same serif emotional line + quiet sans utility,
 *    same spacing tokens." This atom owns those decisions in one place.
 *
 * Migration note: /escape ships with this atom in this pass. /enjoy keeps
 * its inline hero for now (walkthrough-sensitive). A controlled migration
 * of /enjoy lands in a later pass once /escape stabilises.
 *
 * Props:
 *   eyebrow      — quiet uppercase line above headline (string)
 *   headline     — main serif emotional line (ReactNode · can include
 *                  <em>highlight</em> spans). Required.
 *   headlineTail — italic brass tail · matches /enjoy's "We open the door."
 *                  pattern (optional ReactNode)
 *   subtitle     — single sans-serif line under the headline (ReactNode)
 *   children     — slot for the search bar / CTAs / extras (rendered
 *                  below the subtitle, max-width 720 centred)
 *   maxWidth     — child container max width (default 720)
 *   testid       — root data-testid (default "concierge-hero")
 */
import React from "react";

export default function ConciergeHero({
    eyebrow,
    headline,
    headlineTail,
    subtitle,
    children,
    maxWidth = 720,
    testid = "concierge-hero",
}) {
    return (
        <section
            data-testid={testid}
            style={{
                paddingTop: "clamp(16px, 3vw, 28px)",
                paddingBottom: "clamp(14px, 2.5vw, 24px)",
                textAlign: "center",
                paddingInline: 14,
            }}
        >
            {eyebrow && (
                <p
                    className="t-mono uppercase"
                    style={{
                        fontSize: 11,
                        letterSpacing: "0.22em",
                        color: "var(--t-brass)",
                        margin: 0,
                    }}
                    data-testid={`${testid}-eyebrow`}
                >
                    {eyebrow}
                </p>
            )}
            {headline && (
                <h1
                    className="t-serif"
                    style={{
                        // Mobile-first clamp · founder ruling 19 Feb 2026 evening:
                        // hero must feel premium but not oversized on iPhone.
                        // 32px floor on small mobile · 56px ceiling on widescreen.
                        fontSize: "clamp(32px, 6.4vw, 56px)",
                        lineHeight: 1.04,
                        letterSpacing: "-0.015em",
                        color: "var(--t-ink, #211C15)",
                        fontWeight: 500,
                        margin: "12px 0 16px",
                    }}
                    data-testid={`${testid}-headline`}
                >
                    {headline}
                    {headlineTail && (
                        <>
                            {" "}
                            <span
                                data-testid={`${testid}-headline-tail`}
                                style={{
                                    // Founder ruling 19 Feb 2026 evening · brass/gold
                                    // accent on the promise word. Fallback hex used
                                    // so the brass shows even if the page hasn't
                                    // mounted the `[data-table-surface]` wrapper.
                                    // "Black thought · brass promise."
                                    color: "var(--t-brass, #8B6914)",
                                    fontStyle: "italic",
                                }}
                            >
                                {headlineTail}
                            </span>
                        </>
                    )}
                </h1>
            )}
            {subtitle && (
                <p
                    style={{
                        fontSize: "clamp(14px, 1.6vw, 17px)",
                        color: "var(--t-ink2, #5C5447)",
                        lineHeight: 1.55,
                        maxWidth: 640,
                        margin: "0 auto 28px",
                        paddingInline: 14,
                    }}
                    data-testid={`${testid}-subtitle`}
                >
                    {subtitle}
                </p>
            )}
            {children && (
                <div style={{ maxWidth, margin: "0 auto" }}>
                    {children}
                </div>
            )}
        </section>
    );
}
